/**
 * Phase STATE-DRIFT-SWEEP (2026-07-08) — single source of visual truth
 * for every customer-facing status render.
 *
 * The 5 resolvers below replace the ad-hoc STATUS_STYLES / STATUS_LABEL
 * maps + inline `if/else` chains that previously produced two classes
 * of customer-visible bugs:
 *
 *   1. Missing enum member — server produces a status the FE map
 *      doesn't know (`partial_paid`, `partially_signed`, `opened` on
 *      invites) → raw enum text leaks to the customer.
 *   2. Inline duplication — the same "customer done?" answer got
 *      re-computed 3× on `CustomerDashboardPage`, so state changes
 *      required chasing multiple call sites.
 *
 * Every resolver returns a plain object shape so callers can spread /
 * destructure without importing helpers:
 *
 *   { code, label, tone, ...flags }
 *
 * ``tone`` is a Tailwind class chunk ready to drop into `className`.
 * The palette matches the pre-refactor styles so no visual regressions
 * are introduced on the non-drift-risk states.
 *
 * The full audit lives at `/app/memory/STATE_DRIFT_AUDIT.md`.
 */

// ---------- shared palette ----------

const TONE = {
    slate:   "bg-slate-100 text-slate-700 ring-slate-200",
    slateMuted: "bg-slate-100 text-slate-500 ring-slate-200",
    blue:    "bg-blue-50 text-blue-900 ring-blue-200",
    violet:  "bg-violet-50 text-violet-800 ring-violet-200",
    amber:   "bg-amber-50 text-amber-900 ring-amber-200",
    emerald: "bg-emerald-50 text-emerald-900 ring-emerald-200",
    emeraldStrong: "bg-emerald-100 text-emerald-900 ring-emerald-300",
    red:     "bg-red-50 text-red-700 ring-red-200",
    redStrong: "bg-red-50 text-red-800 ring-red-200",
};

// Normalize a candidate date string to a YYYY-MM-DD form using the
// local timezone. Returns null on garbage input so callers can decide
// how to react.
function _todayISO() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}


// ===========================================================
// 1. Invoice — list badge + detail badge + Pay-now gate
// ===========================================================

/**
 * Handles both the stored ``invoice.status`` and the cron-lag between
 * a past-due invoice and the next ``run_overdue_tick`` cycle. Also
 * teaches the FE about the ``partial_paid`` enum member that the
 * pre-STATE-DRIFT-SWEEP code silently rendered as raw uppercase text.
 *
 * Returns:
 *   {
 *     code:        "draft" | "sent" | "viewed" | "overdue" | "paid" | "partial_paid" | "void" | "unknown",
 *     label:       "Sent" | "Overdue" | ...,
 *     tone:        Tailwind class string,
 *     isPayable:   true when the customer can/should be shown a Pay-now button,
 *     isPaid:      true when the invoice has been fully paid,
 *     isOverdue:   true when past due (INCLUDING the cron-lag window),
 *     isPartial:   true when partially paid but still has a residual balance,
 *   }
 */
export function resolveInvoiceStatus(inv) {
    const status = (inv && inv.status) || "draft";
    const total = Number(inv?.total_cents ?? inv?.total ?? 0);
    const paid = Number(inv?.amount_paid_cents ?? 0);
    const dueDate = inv?.due_date || null;
    const today = _todayISO();

    // Detect past-due EVEN WHEN the server still says "sent"/"viewed"
    // (the hourly overdue cron hasn't flipped it yet).
    const pastDue = (
        dueDate
        && ["sent", "viewed", "partial_paid"].includes(status)
        && String(dueDate) < today
    );

    if (status === "paid") {
        return { code: "paid", label: "Paid", tone: TONE.emeraldStrong,
                 isPayable: false, isPaid: true, isOverdue: false, isPartial: false };
    }
    if (status === "void") {
        return { code: "void", label: "Void", tone: "bg-slate-200 text-slate-700 ring-slate-300",
                 isPayable: false, isPaid: false, isOverdue: false, isPartial: false };
    }
    if (status === "overdue" || pastDue) {
        return { code: "overdue", label: "Overdue", tone: TONE.red,
                 isPayable: true, isPaid: false, isOverdue: true,
                 isPartial: status === "partial_paid" || (paid > 0 && paid < total) };
    }
    if (status === "partial_paid") {
        return { code: "partial_paid", label: "Partially paid", tone: TONE.amber,
                 isPayable: true, isPaid: false, isOverdue: false, isPartial: true };
    }
    if (status === "viewed") {
        return { code: "viewed", label: "Viewed", tone: TONE.violet,
                 isPayable: true, isPaid: false, isOverdue: false, isPartial: false };
    }
    if (status === "sent") {
        return { code: "sent", label: "Sent", tone: TONE.blue,
                 isPayable: true, isPaid: false, isOverdue: false, isPartial: false };
    }
    if (status === "draft") {
        return { code: "draft", label: "Draft", tone: TONE.slate,
                 isPayable: false, isPaid: false, isOverdue: false, isPartial: false };
    }
    // Fallback: unknown enum → render its title-case form on slate. We
    // NEVER leak raw underscored enum text.
    const pretty = String(status).replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
    return { code: "unknown", label: pretty, tone: TONE.slate,
             isPayable: false, isPaid: false, isOverdue: false, isPartial: false };
}


// ===========================================================
// 2. Submission — pill + card copy + primary-action button
// ===========================================================

/**
 * Collapses the 3 inline chains on `CustomerDashboardPage` (status
 * pill, "Draft in progress" copy, "Continue" vs "View" button) into
 * a single derivation.
 *
 * Returns:
 *   {
 *     code, label, tone,
 *     summary:     one-line status copy under the H3 ("Draft in progress" / "Equipment assigned — application complete" / ...),
 *     primaryAction: { label, kind: "continue" | "view" | "none" },
 *     isTerminal, isCustomerSideComplete, cancelable,
 *   }
 */
export function resolveSubmissionStatus(sub) {
    const status = (sub && sub.status) || "draft";
    const submittedAt = sub?.submitted_at || null;

    // Terminal / limbo states after admin action.
    if (status === "cancelled") {
        return {
            code: "cancelled", label: "cancelled", tone: TONE.slateMuted,
            summary: "This application was cancelled",
            primaryAction: { label: "", kind: "none" },
            isTerminal: true, isCustomerSideComplete: false, cancelable: false,
        };
    }
    if (status === "cancelled_by_admin") {
        return {
            code: "cancelled_by_admin", label: "cancelled", tone: TONE.redStrong,
            summary: "Application cancelled",
            primaryAction: { label: "", kind: "none" },
            isTerminal: true, isCustomerSideComplete: false, cancelable: false,
        };
    }
    if (status === "awaiting_reassignment") {
        return {
            code: "awaiting_reassignment", label: "awaiting reassignment", tone: TONE.amber,
            summary: "Awaiting equipment reassignment",
            primaryAction: { label: "", kind: "none" },
            isTerminal: true, isCustomerSideComplete: false, cancelable: false,
        };
    }

    // Customer-side complete: application is done, equipment assigned
    // (or approved but pre-assignment). No "Continue" — just "View".
    if (status === "equipment_assigned") {
        return {
            code: "equipment_assigned", label: "equipment assigned", tone: TONE.emerald,
            summary: "Equipment assigned — application complete",
            primaryAction: { label: "View", kind: "view" },
            isTerminal: false, isCustomerSideComplete: true, cancelable: false,
        };
    }
    if (status === "approved") {
        return {
            code: "approved", label: "approved", tone: TONE.emerald,
            summary: "Application approved",
            primaryAction: { label: "View", kind: "view" },
            isTerminal: false, isCustomerSideComplete: true, cancelable: false,
        };
    }
    if (status === "submitted") {
        return {
            code: "submitted", label: "submitted", tone: TONE.blue,
            summary: submittedAt ? `Submitted` : "Submitted",
            primaryAction: { label: "View", kind: "view" },
            isTerminal: false, isCustomerSideComplete: false, cancelable: false,
        };
    }
    if (status === "rejected") {
        return {
            code: "rejected", label: "rejected", tone: TONE.red,
            summary: "Application needs attention",
            primaryAction: { label: "View", kind: "view" },
            isTerminal: false, isCustomerSideComplete: false, cancelable: false,
        };
    }
    // Default: draft. Customer can continue.
    return {
        code: "draft", label: "draft", tone: TONE.amber,
        summary: "Draft in progress",
        primaryAction: { label: "Continue", kind: "continue" },
        isTerminal: false, isCustomerSideComplete: false, cancelable: true,
    };
}


// ===========================================================
// 3. Contract — pill + fully-signed gate
// ===========================================================

/**
 * Every enum member handled explicitly — no ``.replace(/_/g, " ")``
 * fallback that leaks raw values to the customer.
 */
export function resolveContractStatus(c) {
    const status = (c && c.status) || "draft";
    // ``fully_executed`` (new BE enum for both parties signed) + legacy
    // ``fully_signed`` alias both mean the contract is complete.
    if (status === "fully_executed" || status === "fully_signed") {
        return {
            code: "fully_signed", label: "Fully signed", tone: TONE.emerald,
            isFullySigned: true, awaitingCustomerSignature: false, isCancelled: false,
        };
    }
    if (status === "cancelled") {
        return {
            code: "cancelled", label: "Cancelled", tone: TONE.redStrong,
            isFullySigned: false, awaitingCustomerSignature: false, isCancelled: true,
        };
    }
    if (status === "sent_for_signature") {
        return {
            code: "sent_for_signature", label: "Awaiting your signature", tone: TONE.blue,
            isFullySigned: false, awaitingCustomerSignature: true, isCancelled: false,
        };
    }
    // BE enum name ``customer_signed`` (customer done, awaiting admin
    // countersign) + legacy ``partially_signed`` alias.
    if (status === "customer_signed" || status === "partially_signed") {
        return {
            code: "partially_signed", label: "Signed by you — awaiting countersign", tone: TONE.violet,
            isFullySigned: false, awaitingCustomerSignature: false, isCancelled: false,
        };
    }
    if (status === "imported") {
        // Legacy imported contract — signed by both parties out-of-band.
        return {
            code: "imported", label: "Imported (pre-signed)", tone: TONE.emerald,
            isFullySigned: true, awaitingCustomerSignature: false, isCancelled: false,
        };
    }
    if (status === "draft") {
        return {
            code: "draft", label: "Draft — not sent yet", tone: TONE.slate,
            isFullySigned: false, awaitingCustomerSignature: false, isCancelled: false,
        };
    }
    // Unknown fallback → title-case, slate tone.
    const pretty = String(status).replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
    return {
        code: "unknown", label: pretty, tone: TONE.slate,
        isFullySigned: false, awaitingCustomerSignature: false, isCancelled: false,
    };
}


// ===========================================================
// 4. Invite — landing-page status line
// ===========================================================

export function resolveInviteStatus(invite) {
    const status = (invite && invite.status) || "pending";
    if (status === "pending") {
        return { code: "pending", label: "Ready to claim", tone: TONE.emerald };
    }
    if (status === "opened") {
        return { code: "opened", label: "Started — resume when ready", tone: TONE.blue };
    }
    if (status === "submitted") {
        return { code: "submitted", label: "Submitted", tone: TONE.emerald };
    }
    if (status === "expired") {
        return { code: "expired", label: "Expired", tone: TONE.red };
    }
    if (status === "cancelled") {
        return { code: "cancelled", label: "Cancelled", tone: TONE.redStrong };
    }
    // Fallback — title-case.
    const pretty = String(status).replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
    return { code: "unknown", label: pretty, tone: TONE.slate };
}


// ===========================================================
// 5. Submission editability — write-gate reason
// ===========================================================

/**
 * Replaces the single-line ``isLocked = submission.status === "submitted"``
 * check on SubmissionFormPage. Every non-draft state locks the form
 * with a specific reason the FE can show to the customer.
 */
export function resolveSubmissionEditability(sub) {
    const status = (sub && sub.status) || "draft";
    if (status === "draft") {
        return { locked: false, reason: "" };
    }
    if (status === "submitted") {
        return { locked: true, reason: "Application submitted — waiting on our team." };
    }
    if (status === "approved") {
        return { locked: true, reason: "Application approved." };
    }
    if (status === "equipment_assigned") {
        return { locked: true, reason: "Application complete — equipment assigned." };
    }
    if (status === "rejected") {
        return { locked: true, reason: "Application needs attention — please contact us." };
    }
    if (status === "cancelled" || status === "cancelled_by_admin") {
        return { locked: true, reason: "This application was cancelled." };
    }
    if (status === "awaiting_reassignment") {
        return { locked: true, reason: "Awaiting equipment reassignment." };
    }
    // Unknown states — lock defensively rather than let the customer
    // modify data in an undefined state.
    return { locked: true, reason: "This application is not editable." };
}


// ===========================================================
// 6. Toll transaction — list chip + dispute + billability flags
// ===========================================================

/**
 * Phase TOLLBILL-P3a (2026-07-10) — single source of truth for the
 * ``toll_transactions.status`` enum rendered on the admin Tolls page.
 *
 * Backend enum (from ``services/tolls_reconcile.py``):
 *   new | matched | unmatched | disputed | invoiced | void
 *
 * Returns:
 *   {
 *     code, label, tone,
 *     isDisputed:    dispute_flag OR status === "disputed",
 *     isInvoiced:    true when status === "invoiced" (terminal, no dispute allowed),
 *     isMatched:     matched to a customer/rental (invoiceable),
 *     isUnmatched:   awaiting manual reassignment,
 *     isTerminal:    invoiced or void (immutable on re-push per backend guard),
 *   }
 */
export function resolveTollStatus(txn) {
    const status = (txn && txn.status) || "new";
    const disputeFlag = !!(txn && txn.dispute_flag);
    if (status === "invoiced") {
        return {
            code: "invoiced", label: "Invoiced", tone: TONE.emeraldStrong,
            isDisputed: false, isInvoiced: true, isMatched: true,
            isUnmatched: false, isTerminal: true,
        };
    }
    if (status === "void") {
        return {
            code: "void", label: "Void", tone: "bg-slate-200 text-slate-700 ring-slate-300",
            isDisputed: false, isInvoiced: false, isMatched: false,
            isUnmatched: false, isTerminal: true,
        };
    }
    if (status === "disputed") {
        return {
            code: "disputed", label: "Disputed", tone: TONE.red,
            isDisputed: true, isInvoiced: false, isMatched: false,
            isUnmatched: false, isTerminal: false,
        };
    }
    if (status === "matched") {
        return {
            code: "matched", label: "Matched", tone: TONE.blue,
            isDisputed: disputeFlag, isInvoiced: false, isMatched: true,
            isUnmatched: false, isTerminal: false,
        };
    }
    if (status === "unmatched") {
        return {
            code: "unmatched", label: "Unmatched", tone: TONE.amber,
            isDisputed: disputeFlag, isInvoiced: false, isMatched: false,
            isUnmatched: true, isTerminal: false,
        };
    }
    // ``new`` and any unknown — treat as "New / pending".
    return {
        code: "new", label: "New", tone: TONE.slate,
        isDisputed: disputeFlag, isInvoiced: false, isMatched: false,
        isUnmatched: false, isTerminal: false,
    };
}


// Test-visible: expose the palette for snapshot-adjacent tests.
export const __TONE_FOR_TESTING = TONE;


// ===========================================================
// 7. Toll bill — list chip + dispute + billability + overdue flags
// ===========================================================

/**
 * Phase TOLLBILL-P5c (2026-07-10) — single source of truth for the
 * ``toll_bills.status`` enum rendered on the admin Bills page.
 *
 * Shares the same enum shape as `resolveTollStatus` (transactions were
 * the first-class entity in P2-P4c) but is scoped to the bill lifecycle:
 * bills OWN dispute + invoice linkage, and children cascade.
 *
 * Backend enum (from ``services/toll_bills_reconcile.py``):
 *   new | matched | unmatched | disputed | invoiced | void
 *
 * Adds one extra flag over the transaction resolver:
 *   isOverdue:  bill.due_date < today AND not in {invoiced, void}
 *
 * Returns:
 *   { code, label, tone,
 *     isDisputed, isInvoiced, isMatched, isUnmatched, isTerminal,
 *     isOverdue }
 */
export function resolveBillStatus(bill) {
    const status = (bill && bill.status) || "new";
    const disputeFlag = !!(bill && bill.dispute_flag);
    const dueDate = bill?.due_date || null;
    const today = _todayISO();

    let base;
    if (status === "invoiced") {
        base = {
            code: "invoiced", label: "Invoiced", tone: TONE.emeraldStrong,
            isDisputed: false, isInvoiced: true, isMatched: true,
            isUnmatched: false, isTerminal: true,
        };
    } else if (status === "void") {
        base = {
            code: "void", label: "Void", tone: "bg-slate-200 text-slate-700 ring-slate-300",
            isDisputed: false, isInvoiced: false, isMatched: false,
            isUnmatched: false, isTerminal: true,
        };
    } else if (status === "disputed") {
        base = {
            code: "disputed", label: "Disputed", tone: TONE.red,
            isDisputed: true, isInvoiced: false, isMatched: false,
            isUnmatched: false, isTerminal: false,
        };
    } else if (status === "matched") {
        base = {
            code: "matched", label: "Matched", tone: TONE.blue,
            isDisputed: disputeFlag, isInvoiced: false, isMatched: true,
            isUnmatched: false, isTerminal: false,
        };
    } else if (status === "unmatched") {
        base = {
            code: "unmatched", label: "Unmatched", tone: TONE.amber,
            isDisputed: disputeFlag, isInvoiced: false, isMatched: false,
            isUnmatched: true, isTerminal: false,
        };
    } else {
        base = {
            code: "new", label: "New", tone: TONE.slate,
            isDisputed: disputeFlag, isInvoiced: false, isMatched: false,
            isUnmatched: false, isTerminal: false,
        };
    }
    const isOverdue = !!(dueDate && String(dueDate) < today && !base.isTerminal);
    return { ...base, isOverdue };
}
